import { useState, useEffect } from "react";
import { MessageSquare, Send, ArrowLeft, Clock, User } from "lucide-react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { ScrollArea } from "./ui/scroll-area";
import { Badge } from "./ui/badge";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChatList = () => {
  const { user, token } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/chat/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setLoading(false);
    }
  };

  const selectConversation = async (conv) => {
    setSelectedConv(conv);
    try {
      const response = await axios.get(
        `${API}/chat/conversations/${conv.id}/messages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(response.data);
      
      // Update unread count locally
      setConversations(prevConvs => 
        prevConvs.map(c => c.id === conv.id ? { ...c, unread_count: 0 } : c)
      );
    } catch (error) {
      console.error("Error fetching messages:", error);
      toast.error("Eroare la încărcarea mesajelor");
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConv) return;

    setSending(true);
    try {
      const response = await axios.post(
        `${API}/chat/conversations/${selectedConv.id}/messages`,
        { content: newMessage, listing_id: selectedConv.listing_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages([...messages, response.data]);
      setNewMessage("");
      
      // Update last message in conversation list
      setConversations(prevConvs =>
        prevConvs.map(c => c.id === selectedConv.id 
          ? { ...c, last_message: response.data, updated_at: response.data.created_at }
          : c
        )
      );
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Eroare la trimiterea mesajului");
    } finally {
      setSending(false);
    }
  };

  // Auto-refresh messages
  useEffect(() => {
    if (selectedConv) {
      const interval = setInterval(() => {
        selectConversation(selectedConv);
      }, 15000);
      return () => clearInterval(interval);
    }
  }, [selectedConv]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="text-center py-12">
        <MessageSquare className="w-16 h-16 text-stone-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-stone-700 mb-2">Nicio conversație</h3>
        <p className="text-stone-500">Nu ai nicio conversație activă.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[500px]">
      {/* Conversations List */}
      <div className={`md:col-span-1 border rounded-xl overflow-hidden ${selectedConv ? 'hidden md:block' : ''}`}>
        <div className="bg-stone-50 p-3 border-b">
          <h3 className="font-semibold text-stone-700 flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Conversații
          </h3>
        </div>
        <ScrollArea className="h-[440px]">
          {conversations.map((conv) => (
            <button
              key={conv.id}
              data-testid={`conversation-${conv.id}`}
              onClick={() => selectConversation(conv)}
              className={`w-full p-4 text-left border-b hover:bg-stone-50 transition-colors ${
                selectedConv?.id === conv.id ? 'bg-purple-50' : ''
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-purple-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium text-stone-800 truncate">
                      {conv.other_user?.name || "Utilizator"}
                    </p>
                    {conv.unread_count > 0 && (
                      <Badge className="bg-purple-600 text-white text-xs px-2">
                        {conv.unread_count}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-stone-500 truncate">
                    {conv.listing?.title || "Proprietate"}
                  </p>
                  {conv.last_message && (
                    <p className="text-xs text-stone-400 truncate mt-1">
                      {conv.last_message.content}
                    </p>
                  )}
                </div>
              </div>
            </button>
          ))}
        </ScrollArea>
      </div>

      {/* Messages Area */}
      <div className={`md:col-span-2 border rounded-xl overflow-hidden flex flex-col ${!selectedConv ? 'hidden md:flex' : ''}`}>
        {selectedConv ? (
          <>
            {/* Chat Header */}
            <div className="bg-purple-600 text-white p-3 flex items-center gap-3">
              <button 
                onClick={() => setSelectedConv(null)}
                className="md:hidden w-8 h-8 rounded-full hover:bg-white/20 flex items-center justify-center"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <User className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">
                  {selectedConv.other_user?.name || "Utilizator"}
                </p>
                <p className="text-purple-200 text-sm truncate">
                  {selectedConv.listing?.title || "Proprietate"}
                </p>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-3">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.sender_id === user?.id ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                        msg.sender_id === user?.id
                          ? "bg-purple-600 text-white rounded-br-md"
                          : "bg-stone-100 text-stone-800 rounded-bl-md"
                      }`}
                    >
                      <p className="text-sm">{msg.content}</p>
                      <p className={`text-xs mt-1 flex items-center gap-1 ${
                        msg.sender_id === user?.id ? "text-purple-200" : "text-stone-400"
                      }`}>
                        <Clock className="w-3 h-3" />
                        {new Date(msg.created_at).toLocaleTimeString("ro-RO", {
                          hour: "2-digit",
                          minute: "2-digit"
                        })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>

            {/* Input */}
            <form onSubmit={sendMessage} className="p-3 border-t bg-stone-50">
              <div className="flex gap-2">
                <Input
                  data-testid="chat-list-input"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Scrie un mesaj..."
                  disabled={sending}
                  className="flex-1"
                />
                <Button 
                  data-testid="chat-list-send-button"
                  type="submit" 
                  disabled={sending || !newMessage.trim()}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {sending ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </form>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-center p-8">
            <div>
              <MessageSquare className="w-16 h-16 text-stone-300 mx-auto mb-4" />
              <p className="text-stone-500">Selectează o conversație pentru a vedea mesajele</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatList;
